import { Enums } from "@/helpers/enums";
import { Ajax, MethodType } from "@/helpers/web";
import { appData, C, J } from "@/helpers/global";
import { Constants } from "@/helpers/constants";

import { ILogger, LogLevel, HttpTransportType, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import store from "@/store";
import router from '@/router';
import { StoreUser, StoreMessage } from "@/store/constants";
import { UserService } from "@/services/api/user";

const messageListener = {
    connection: null,
    startPromise: null,
    stopPromise: null,
    retryTimer: null,
    sound: null,
    enabled: false,
    syncing: false,
    receivedIds: new Set()
};

const messageReadQueue = {
    pending: new Map(),
    confirmed: new Map(),
    timer: null,
    inFlight: false,
    version: 0
};

const scheduleMessageReadQueue = (service) => {
    if (messageReadQueue.inFlight || messageReadQueue.timer !== null || messageReadQueue.pending.size === 0) {
        return;
    }

    messageReadQueue.timer = setTimeout(() => {
        messageReadQueue.timer = null;

        if (messageReadQueue.inFlight) {
            return;
        }

        const ready = [];

        for (const [owner, item] of messageReadQueue.pending) {
            if (!item.canRead()) {
                messageReadQueue.pending.delete(owner);
                continue;
            }

            const confirmedId = messageReadQueue.confirmed.get(item.key) || 0;

            if (item.endId <= confirmedId) {
                messageReadQueue.pending.delete(owner);
                item.onSaved?.(confirmedId);
                continue;
            }

            ready.push({ owner, item });
        }

        if (ready.length === 0) {
            return;
        }

        const key = ready[0].item.key;
        const batch = ready.filter((entry) => entry.item.key === key);
        const endId = Math.max(...batch.map((entry) => entry.item.endId));
        const version = messageReadQueue.version;

        for (const entry of batch) {
            messageReadQueue.pending.delete(entry.owner);
        }

        messageReadQueue.inFlight = true;
        let settled = false;

        const finish = (success) => {
            if (settled) {
                return;
            }

            settled = true;
            messageReadQueue.inFlight = false;

            if (version === messageReadQueue.version) {
                if (success) {
                    messageReadQueue.confirmed.set(key, Math.max(messageReadQueue.confirmed.get(key) || 0, endId));
                    store.dispatch(StoreMessage.setListReaded, endId);

                    for (const entry of batch) {
                        entry.item.onSaved?.(endId);
                    }
                } else {
                    for (const [owner, item] of messageReadQueue.pending) {
                        if (item.key === key) {
                            messageReadQueue.pending.delete(owner);
                        }
                    }
                }
            }

            scheduleMessageReadQueue(service);
        };

        try {
            const request = service.SaveIsReaded(endId, () => finish(true));

            if (request?.catch) {
                request.catch((error) => {
                    finish(false);
                    console.warn("Mesaj okundu bilgisi gönderilemedi:", error);
                });
            }
        } catch (error) {
            finish(false);
            console.warn("Mesaj okundu bilgisi gönderilemedi:", error);
        }
    }, 200);
};

export const MessageService = {
    List(userId, callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.MESSAGE + "/" + userId, null, wait).then((data) => {
            callback(data);
        });
    },
    ListUser(callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.MESSAGE + "/user", null, wait).then((data) => {
            callback(data);
        });
    },
    ListStudent(callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.MESSAGE + "/student", null, wait).then((data) => {
            callback(data);
        });
    },
    Save(j, callback, wait) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.MESSAGE, j, wait).then((data) => {
            callback(data);
        });
    },
    SaveIsReaded(id, callback) {
        const j = { id };
        Ajax.Api(MethodType.Put, Enums.SERVICE.MESSAGE + "/is-readed", j).then((data) => {
            callback(data);
        });
    },
    Delete(id, callback) {
        const j = {};
        Ajax.Api(MethodType.Delete, Enums.SERVICE.MESSAGE + "/" + id, j).then((data) => {
            callback(data);
        });
    },
    async ListenServer() {
        const service = this;

        if (messageListener.stopPromise) {
            await messageListener.stopPromise;
        }

        if (messageListener.startPromise) {
            return messageListener.startPromise;
        }

        if (messageListener.connection && messageListener.connection.state !== HubConnectionState.Disconnected) {
            return messageListener.connection.state !== HubConnectionState.Disconnecting;
        }

        messageListener.enabled = true;
        clearTimeout(messageListener.retryTimer);
        messageListener.retryTimer = null;

        const retry = () => {
            if (!messageListener.enabled || messageListener.retryTimer !== null) {
                return;
            }

            messageListener.retryTimer = setTimeout(() => {
                messageListener.retryTimer = null;

                if (messageListener.enabled && router.currentRoute.value.meta.theme === Enums.THEME.CLASSIC) {
                    service.ListenServer().catch((error) => console.warn("Message retry error:", error));
                }
            }, 5000);
        };

        const syncUnread = async (connection) => {
            if (!messageListener.enabled || connection !== messageListener.connection || connection.state !== HubConnectionState.Connected) {
                return;
            }

            messageListener.syncing = true;

            try {
                const user = store.getters[StoreUser.get];
                const isStudent = C.Int(user.userType) === C.Int(Enums.USER_TYPE.STUDENT);
                const endDate = store.getters[StoreMessage.getEndDateUnReaded](isStudent);

                await connection.invoke("receiveMessageGetUnReaded", endDate);
            } catch (error) {
                console.warn("Unread message sync error:", error);
            } finally {
                messageListener.syncing = false;
            }
        };

        messageListener.startPromise = Promise.resolve().then(async () => {
            const loggedIn = await UserService.GetTokenIsLogin();

            if (!messageListener.enabled) {
                return false;
            }

            if (!loggedIn) {
                retry();
                return false;
            }

            if (!messageListener.connection) {
                const connection = new HubConnectionBuilder()
                    .withUrl(appData.serverBase + "hubs/user/message", {
                        accessTokenFactory: () => UserService.GetAccessRefreshToken(),
                        transport: HttpTransportType.WebSockets
                    })
                    .configureLogging(LogLevel.Critical)
                    .withAutomaticReconnect([0, 3000, 5000, 10000])
                    .build();

                messageListener.connection = connection;

                const receive = (message) => {
                    if (!messageListener.enabled || connection !== messageListener.connection) {
                        return;
                    }

                    try {
                        const id = C.Int(message.id);
                        const exists = (store.state.message?.list || []).some((item) => C.Int(item.id) === id);
                        const isNew = id > 0 && !exists && !messageListener.receivedIds.has(id);

                        if (id > 0) {
                            messageListener.receivedIds.add(id);

                            if (messageListener.receivedIds.size > 2000) {
                                messageListener.receivedIds.delete(messageListener.receivedIds.values().next().value);
                            }
                        }

                        message.createdDate = typeof message.createdDate === "string"
                            ? message.createdDate.replace("Z", "")
                            : message.createdDate;

                        store.dispatch(StoreMessage.setList, [message]);

                        const user = store.getters[StoreUser.get];
                        const isStudent = C.Int(user.userType) === C.Int(Enums.USER_TYPE.STUDENT);

                        if (!isNew || messageListener.syncing || C.Bool(message.isReaded) || C.Bool(message.isStudent) === isStudent) {
                            return;
                        }

                        if (!messageListener.sound) {
                            messageListener.sound = new Audio("/assets/sound/message.mp3");
                        }

                        if (messageListener.sound.paused) {
                            messageListener.sound.play().catch((error) => console.warn("Message sound error:", error));
                        }
                    } catch (error) {
                        console.warn("Message processing error:", error);
                    }
                };

                connection.on("receiveError", (error) => console.warn("Received error:", error));

                connection.on("receiveMessageAdd", (id, content, teacherId, isStudent, isReaded, createdDate, fullName) => {
                    receive({ id, content, teacherId, isStudent, isReaded, createdDate, fullName });
                });

                connection.on("receiveMessageTeacherAdd", (id, content, studentId, isStudent, isReaded, createdDate, fullName) => {
                    receive({ id, content, studentId, isStudent, isReaded, createdDate, fullName });
                });

                connection.onreconnecting(() => {
                    messageListener.syncing = true;
                });

                connection.onreconnected(() => syncUnread(connection));
                connection.onclose(retry);
            }

            messageListener.syncing = true;
            await messageListener.connection.start();

            if (!messageListener.enabled) {
                return false;
            }

            await syncUnread(messageListener.connection);

            return messageListener.enabled && messageListener.connection?.state === HubConnectionState.Connected;
        }).catch((error) => {
            messageListener.syncing = false;
            console.error("Message connection error:", error);
            retry();
            throw error;
        }).finally(() => {
            messageListener.startPromise = null;
        });

        return messageListener.startPromise;
    },
    StopListenServer() {
        this.ResetReadQueue();
        if (messageListener.stopPromise) {
            return messageListener.stopPromise;
        }

        messageListener.enabled = false;
        clearTimeout(messageListener.retryTimer);
        messageListener.retryTimer = null;

        messageListener.stopPromise = Promise.resolve().then(async () => {
            try {
                if (messageListener.startPromise) {
                    await messageListener.startPromise.catch(() => {});
                }

                const connection = messageListener.connection;
                messageListener.connection = null;

                if (connection) {
                    await connection.stop();
                }
            } finally {
                messageListener.sound?.pause();
                messageListener.sound = null;
                messageListener.syncing = false;
                messageListener.receivedIds.clear();
            }
        }).finally(() => {
            messageListener.stopPromise = null;
        });

        return messageListener.stopPromise;
    },

    QueueIsReaded(owner, endId, isStudent, partnerId, canRead, onSaved) {
        const id = C.Int(endId);
        const partner = C.Int(partnerId);

        if (id <= 0 || partner <= 0 || !canRead()) {
            this.CancelQueuedRead(owner);
            return;
        }

        const key = `${C.Bool(isStudent)}:${partner}`;
        const confirmedId = messageReadQueue.confirmed.get(key) || 0;

        if (id <= confirmedId) {
            messageReadQueue.pending.delete(owner);
            onSaved?.(confirmedId);
            return;
        }
        messageReadQueue.pending.set(owner, { key, endId: id, canRead, onSaved });
        scheduleMessageReadQueue(this);
    },
    CancelQueuedRead(owner) {
        messageReadQueue.pending.delete(owner);

        if (messageReadQueue.pending.size === 0) {
            clearTimeout(messageReadQueue.timer);
            messageReadQueue.timer = null;
        }
    },
    ResetReadQueue() {
        messageReadQueue.version += 1;
        clearTimeout(messageReadQueue.timer);
        messageReadQueue.timer = null;
        messageReadQueue.pending.clear();
        messageReadQueue.confirmed.clear();
    },
};
  